import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
// ...existing code...
import ProjectsGrid from '../components/ProjectsGrid';

// Utility to get API base URL depending on environment
const getApiBaseUrl = () => {
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return 'http://localhost:5000';
  }
  // Use relative URL for production
  return '';
};

// Utility to check if user is logged in
const isLoggedIn = !!localStorage.getItem('token');

const ProjectPage = () => {
  const { slug } = useParams(); // Get the slug from the URL
  const [projectId, setProjectId] = useState(null); // Will be set after fetching project by slug
  const navigate = useNavigate(); // Initialize useNavigate
  const [project, setProject] = useState(null);
  const [likes, setLikes] = useState(0); // State to store the number of likes
  const [liked, setLiked] = useState(false); // Track if user already liked
  const [tools, setTools] = useState([]); // State to store tools
  const [objects, setObjects] = useState([]); // State to store objects
  const [categories, setCategories] = useState([]); // State to store categories
  const [isAdmin, setIsAdmin] = useState(false); // State to store admin status
  // ...existing code...
  const [similarProjects, setSimilarProjects] = useState([]); // New state for similar projects
  const [flagged, setFlagged] = useState(false); // State for flagging
  const [flagError, setFlagError] = useState('');
  // Handler for flagging the project
  const handleFlagProject = () => {
    if (!projectId) return;
    fetch(`${getApiBaseUrl()}/api/projects/${projectId}/flag`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setFlagged(true);
          setFlagError('');
        } else {
          setFlagError('Erreur lors du signalement.');
        }
      })
      .catch(() => setFlagError('Erreur lors du signalement.'));
  };

  useEffect(() => {
    // If slug is missing or 'undefined', redirect to no-results page
    if (!slug || slug === 'undefined') {
      navigate('/no-results');
      return;
    }
    // Fetch project by slug
    fetch(`${getApiBaseUrl()}/api/projects/slug/${slug}`)
      .then((response) => {
        if (!response.ok) {
          navigate('/no-results');
          return null;
        }
        return response.json();
      })
      .then((data) => {
        if (!data || !data.id) {
          navigate('/no-results');
          return;
        }
        setProject(data);
        setLikes(data.likes || 0);
        setProjectId(data.id);
        // Now fetch related data by projectId
        // If logged in, fetch liked projects to check if already liked
        const token = localStorage.getItem('token');
        if (token) {
          fetch(`${getApiBaseUrl()}/api/users/liked-projects`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          })
            .then((response) => response.ok ? response.json() : { projects: [] })
            .then((data2) => {
              if (Array.isArray(data2.projects)) {
                setLiked(data2.projects.some((p) => String(p.id) === String(data.id)));
              } else {
                setLiked(false);
              }
            })
            .catch(() => setLiked(false));
        } else {
          setLiked(false);
        }

        // Fetch tools for the project
        fetch(`${getApiBaseUrl()}/api/projects/${data.id}/tools`)
          .then((response) => response.json())
          .then((toolsData) => {
            const sortedTools = toolsData.sort((a, b) => a.name.localeCompare(b.name));
            setTools(sortedTools);
          })
          .catch((error) => console.error('Error fetching tools:', error));

        // Fetch categories for the project
        fetch(`${getApiBaseUrl()}/api/projects/${data.id}/categories`)
          .then((response) => response.json())
          .then((catData) => {
            const sortedCategories = catData.sort((a, b) => a.name.localeCompare(b.name));
            setCategories(sortedCategories);
          })
          .catch((error) => {
            console.error('Error fetching categories:', error);
            setCategories([]);
          });

        // Fetch objects for the project
        fetch(`${getApiBaseUrl()}/api/projects/${data.id}/objects`)
          .then((response) => response.json())
          .then((objData) => {
            const sortedObjects = objData.sort((a, b) => a.name.localeCompare(b.name));
            setObjects(sortedObjects);

            // Fetch similar projects based on these objects (reuse endpoint)
            if (objData.length > 0) {
              const objectIds = objData.map(obj => obj.id);
              Promise.all(
                objectIds.map(objId =>
                  fetch(`${getApiBaseUrl()}/api/objects/${objId}/projects`).then(res => res.json())
                )
              ).then(results => {
                const allProjects = results.flat().filter(p => p.id !== Number(data.id));
                const uniqueProjects = [];
                const seen = new Set();
                for (const p of allProjects) {
                  if (!seen.has(p.id)) {
                    seen.add(p.id);
                    uniqueProjects.push(p);
                  }
                }
                setSimilarProjects(uniqueProjects);
              });
            } else {
              setSimilarProjects([]);
            }
          })
          .catch((error) => {
            console.error('Error fetching objects:', error);
            setSimilarProjects([]);
          });
      })
      .catch((error) => {
        console.error('Error fetching project:', error);
        navigate('/no-results');
      });
  }, [slug, navigate]);

  const handleLike = () => {
    if (!isLoggedIn) {
      navigate('/create-user');
      return;
    }
    if (liked || !projectId) {
      return;
    }
    // Only like the project, do not redirect for connected users
    fetch(`${getApiBaseUrl()}/api/projects/${projectId}/like`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setLikes((prevLikes) => prevLikes + 1); // Increment likes locally
          setLiked(true);
        }
      })
      .catch((error) => console.error('Error liking project:', error));
  };

  const handleShare = () => {
    // Logic to handle sharing the project
    let slugStr = '';
    if (project && project.name) {
      slugStr = project.name
        .toString()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase();
    }
    const projectUrl = `${window.location.origin}/project/${slugStr}`;
    navigator.clipboard.writeText(projectUrl).then(() => {
      alert('Project link copied to clipboard!');
    });
  };

  const handleDeleteProject = () => {
    if (!projectId) return;
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
      fetch(`${getApiBaseUrl()}/api/projects/${projectId}`, {
        method: 'DELETE',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to delete project');
          }
          return response.json();
        })
        .then(() => {
          alert('Projet supprimé avec succès');
          navigate('/'); // Redirect to homepage
        })
        .catch((error) => {
          console.error('Error deleting project:', error);
          alert('Erreur lors de la suppression du projet');
        });
    }
  };

  const handleHeartClick = () => {
    handleLike();
  };

  if (!project) {
    return <div>Loading...</div>;
  }

  // Admin edit mode removed


  // Normal view
  return (
    <div className="projectContainer" style={{ maxWidth: 600, margin: '32px auto', padding: 0, background: 'white', borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
      <Helmet>
        <title>
          {project && project.name
            ? `Projet DIY : ${project.name} - Tutoriel, étapes et matériel`
            : 'Projet DIY : Tutoriel, étapes et matériel'}
        </title>
        <meta
          name="description"
          content={
            project && project.name
              ? `Découvrez comment réaliser le projet "${project.name}" étape par étape : tutoriel, matériel nécessaire, astuces et conseils.`
              : 'Découvrez des tutoriels DIY, étapes de réalisation et matériel pour vos projets.'
          }
        />
        <link rel="canonical" href={`https://0123456789.fr/project/${slug}`} />
      </Helmet>
      {/* Project Image Banner - fully visible image */}
      <div style={{ width: '100%', maxHeight: 400, background: '#f3f3f3', borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <img 
          src={project.after_photo} 
          alt="Sauvetage" 
          style={{
            maxHeight: 400,
            width: 'auto',
            maxWidth: '100%',
            height: 'auto',
            display: 'block',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            margin: '0 auto',
            objectFit: 'contain',
            background: '#f3f3f3',
          }} 
        />
      </div>

      {/* Title Row - now below the image, not overlapping */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 24, marginBottom: 24, padding: '0 18px' }}>
        <h1 className="projectName" style={{ fontSize: 26, fontWeight: 700, margin: 0, flex: 1, textAlign: 'center', color: '#222', background: 'white', borderRadius: 8, padding: '8px 0', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
          {`Fabriquer un(e) ${project.name.toLowerCase()}`}
          <br />
          <span style={{ fontSize: 22, fontWeight: 500, color: '#2ecc40', display: 'inline-block', marginTop: 4 }}>
            {`(DIY ${
              project.difficulty === 1 ? 'facile' :
              project.difficulty === 2 ? 'accessible' :
              project.difficulty === 3 ? 'avancé' : 'facile'
            })`}
          </span>
        </h1>
      </div>


      {/* Info Row: Time, Difficulty, Categories */}
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        gap: 0,
        margin: '0 18px 24px 18px',
        background: '#f6f6f6',
        borderRadius: 12,
        boxShadow: '0 1px 4px rgba(0,0,0,0.03)'
      }}>
        {/* Time */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 14, borderRight: '1px solid #eee' }}>
          <img src="/assets/site/sand-clock.svg" alt="Durée" style={{ width: 32, height: 32, marginBottom: 6 }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#2ecc40' }}>
            {project.difficulty === 1 ? '10mn' : project.difficulty === 2 ? '30mn' : project.difficulty === 3 ? '1h' : '10mn'}
          </span>
        </div>
        {/* Difficulty */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 14, borderRight: '1px solid #eee' }}>
          <img src="/assets/site/tool-box.svg" alt="Difficulté" style={{ width: 32, height: 32, marginBottom: 6 }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#2ecc40', textAlign: 'center' }}>
            {project.difficulty === 1 ? 'Facile' : project.difficulty === 2 ? 'Modéré' : project.difficulty === 3 ? 'Avancé' : 'Facile'}
          </span>
        </div>
        {/* Categories */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 14 }}>
          <img src="/assets/site/recycle.svg" alt="Catégories" style={{ width: 32, height: 32, marginBottom: 6 }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#2ecc40', textAlign: 'center' }}>
            {categories && categories.length > 0
              ? categories.map(cat => cat.name.toLowerCase()).join(', ')
              : 'aucune'}
          </span>
        </div>
      </div>

      {/* Resume Section */}
      <div style={{ background: '#f6f6f6', borderRadius: 12, margin: '0 18px 24px 18px', padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.03)', textAlign: 'center', fontSize: 17, fontWeight: 500, color: '#333' }}>
        {(() => {
          let time = '10 minutes';
          if (project.difficulty === 2) time = '30 minutes';
          else if (project.difficulty === 3) time = '1 heure';
          return `Fabriquez un(e) simple ${project.name ? project.name.toLowerCase() : ''} en ${time} grâce à ce tutoriel simple et accessible.`;
        })()}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 16 }}>
          <div
            className="heartContainer"
            style={{
              width: 48,
              height: 48,
              background: 'white',
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: liked ? 'not-allowed' : 'pointer',
              position: 'relative',
              opacity: liked ? 0.5 : 1,
              pointerEvents: liked ? 'none' : 'auto',
            }}
            onClick={handleHeartClick}
            title={!isLoggedIn ? "Connectez-vous pour aimer" : liked ? "Déjà aimé" : "Aimer"}
          >
            <img src="/assets/site/heart.svg" alt="Heart Icon" style={{ width: 32, height: 32, filter: 'invert(50%) sepia(80%) saturate(500%) hue-rotate(100deg) brightness(90%) contrast(85%)' }} />
            <span style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 15,
              fontWeight: 700,
              color: 'white',
              background: 'none',
              border: 'none',
              padding: 0,
              zIndex: 3,
              minWidth: 0,
              minHeight: 0,
              display: 'block',
              textAlign: 'center',
              lineHeight: 1
            }}>{likes}</span>
          </div>
          <div className="shareContainer" style={{ width: 40, height: 40, background: 'white', borderRadius: '50%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={handleShare}>
            <img src="/assets/site/share.svg" alt="Share Icon" style={{ width: 26, height: 26, filter: 'invert(50%) sepia(80%) saturate(500%) hue-rotate(100deg) brightness(90%) contrast(85%)' }} />
          </div>
        </div>
      </div>

      {/* Tools Section (Matériel nécessaire) */}
      <div style={{ background: '#f6f6f6', borderRadius: 12, margin: '0 18px 24px 18px', padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.03)', width: 'calc(100% - 36px)', maxWidth: 564 }}>
        <h2 className="sectionTitle" style={{ fontSize: 20, fontWeight: 600, marginBottom: 10, color: '#2ecc40', paddingLeft: 18 }}>Matériel nécessaire</h2>
        {tools.length > 0 ? (
          <ul style={{ listStyleType: 'none', padding: '0 0 0 18px', margin: 0 }}>
            {tools.map((tool) => (
              <li key={tool.id} style={{ fontSize: 16, marginBottom: 5, color: '#333', fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#2ecc40', marginRight: 10, minWidth: 8 }}></span>
                {tool.name}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ color: '#888', fontStyle: 'italic', margin: 0, paddingLeft: 18 }}>Ce projet n'a pas mobilisé d'outils</p>
        )}
      </div>

      {/* Steps Section */}
      <div style={{ background: '#f9f9f9', borderRadius: 12, margin: '0 18px 24px 18px', padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.03)', width: 'calc(100% - 36px)', maxWidth: 564 }}>
        <h2 className="sectionTitle" style={{ fontSize: 20, fontWeight: 600, marginBottom: 10, color: '#2ecc40', paddingLeft: 18 }}>Etapes de réalisation</h2>
        {project.description ? (
          <ol style={{ textAlign: 'left', padding: '0 0 0 24px', listStyleType: 'decimal', fontSize: 16, lineHeight: 1.8, margin: 0 }}>
            {project.description.split('\n').map((step, index) => (
              <li key={index} style={{ marginBottom: 10 }}>{step}</li>
            ))}
          </ol>
        ) : (
          <p style={{ color: '#888', fontStyle: 'italic', margin: 0, paddingLeft: 18 }}>Aucune étape indiquée pour ce sauvetage</p>
        )}
      </div>

      {/* Similar Projects Section */}
      <div style={{ background: '#f9f9f9', borderRadius: 12, margin: '0 18px 0 18px', padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
        <h2 className="sectionTitle" style={{ fontSize: 20, fontWeight: 600, marginBottom: 10, color: '#2ecc40' }}>
          {(() => {
            if (categories && categories.length > 0) {
              const catNames = categories.map(cat => cat.name.toLowerCase());
              let catStr = '';
              if (catNames.length === 1) catStr = catNames[0];
              else catStr = catNames.slice(0, -1).join(', ') + ' ou des ' + catNames[catNames.length - 1];
              return `Découvrez aussi nos idées pour recycler des ${catStr}`;
            } else {
              return 'Découvrez aussi nos idées pour recycler';
            }
          })()}
        </h2>
        {similarProjects.length > 0 ? (
          <ProjectsGrid projects={similarProjects} onProjectClick={slugStr => navigate(`/project/${slugStr}`)} hideIcons={true} />
        ) : (
          <p style={{ color: '#888', fontStyle: 'italic', margin: 0 }}>Aucun autre projet n'a encore sauvé ces objets.</p>
        )}
      </div>


      {/* Flag Content Button */}
      <div style={{ margin: '32px 0 0 0', textAlign: 'center' }}>
        {flagged ? (
          <div style={{ color: '#e67e22', fontWeight: 600, fontSize: 17, marginBottom: 12 }}>Merci, ce contenu a été signalé.</div>
        ) : (
          <button
            style={{
              background: '#f39c12',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '12px 32px',
              fontWeight: 700,
              fontSize: 17,
              cursor: 'pointer',
              marginBottom: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
            onClick={handleFlagProject}
            disabled={flagged}
          >
            Signaler ce contenu
          </button>
        )}
        {flagError && <div style={{ color: 'red', marginTop: 8 }}>{flagError}</div>}
      </div>
    </div>
  );
};


export default ProjectPage;